/**
 * 20260801 — W1 load-perf instrumentation. TEMPORARY: this whole directory is meant to be deleted
 * once the post-reload board wall is closed out.
 *
 * Aggregate counters ONLY. The thing being measured is a 317-message cadence, so per-message
 * logging is not an option: 317 diagLogs (each a JSON.stringify plus an outbox push and a
 * previspro:// ping) would cost more than the wall they were meant to report. Every site here
 * accumulates into a module-level counter; exactly one line is emitted per load, immediately
 * before the viewReady reply.
 *
 * READING THE NUMBERS: the counters reset only inside emitLoadPerf(), i.e. on allScenesReady. So
 * `n=` and the sums are PER EMISSION, not per load — a load that never receives an allScenesReady
 * (a lost handshake, a torn-down WebView) folds its counts into the NEXT emitted line. Visible in
 * the test suite, where one line reports every message the file delivered rather than the two its
 * own case sent. Self-correcting and diagnostic-only, but don't compare two device runs without
 * checking each was preceded by exactly one allScenesReady.
 *
 * SHIPPING: diagLog is NOT flag-gated (transport.ts sends whenever capabilities.bridge), so all
 * four [Perf] lines reach every embedded user until this directory is deleted. That was sanctioned
 * for this measurement; it is not a permanent state.
 *
 * Removing it: delete src/perf/ and the four import sites — bridge/features/timeline.ts,
 * store/projectStore.ts, store/actions.ts, components/SceneBoard/SceneBoardExpanded.tsx. The
 * viewReady-egress twin deliberately does NOT live here (it would import bridge/transport.ts,
 * which would import this back); it is module-local inside transport.ts, same lifetime.
 */

import { diagLog } from '@/bridge/transport';

function ms(value: number): string {
  return value.toFixed(1);
}

// ── sceneReady burst (bridge/features/timeline.ts) ────────────────────────

const burst = { count: 0, handlerSum: 0, max: 0, gapSum: 0, firstAt: -1, lastAt: -1 };

/**
 * Call at the END of the sceneReady handler with the performance.now() taken at its top.
 * gapSum is the time NOT spent in the handler between consecutive messages — with the burst
 * coalesced it is the whole cost of Unity's push cadence, which is what the fix leaves behind.
 */
export function recordSceneReady(startedAt: number): void {
  const now = performance.now();
  const elapsed = now - startedAt;
  if (burst.firstAt < 0) burst.firstAt = startedAt;
  else burst.gapSum += startedAt - burst.lastAt;
  burst.lastAt = now;
  burst.count += 1;
  burst.handlerSum += elapsed;
  if (elapsed > burst.max) burst.max = elapsed;
}

// ── coalesced store write (store/projectStore.ts) ─────────────────────────

const store = { setMs: 0 };

/** Time inside markScenesReady's single set(), summed over however many flushes a load took. */
export function recordStoreSet(elapsedMs: number): void {
  store.setMs += elapsedMs;
}

// ── board render (components/SceneBoard/SceneBoardExpanded.tsx) ───────────

const board = { renders: 0, bodyMs: 0, commitMs: 0, scenes: 0, cells: 0 };

/**
 * Call from a no-dep useLayoutEffect, so `now` is the post-commit instant.
 * bodyMs = the component body alone; commitMs = body entry through children + DOM commit.
 */
export function recordBoardRender(
  bodyStartedAt: number, bodyEndedAt: number, scenes: number, cells: number,
): void {
  board.renders += 1;
  board.bodyMs += bodyEndedAt - bodyStartedAt;
  board.commitMs += performance.now() - bodyStartedAt;
  board.scenes = scenes;
  board.cells = cells;
}

// ── emission ──────────────────────────────────────────────────────────────

/** Zeroed after every emission so a second reload in the same session measures only itself. */
function reset(): void {
  burst.count = 0; burst.handlerSum = 0; burst.max = 0; burst.gapSum = 0;
  burst.firstAt = -1; burst.lastAt = -1;
  store.setMs = 0;
  board.renders = 0; board.bodyMs = 0; board.commitMs = 0; board.scenes = 0; board.cells = 0;
}

/** The one line. Emitted from the allScenesReady handler, immediately before sendToNative('viewReady'). */
export function emitLoadPerf(): void {
  const sinceFirst = burst.firstAt < 0 ? 0 : performance.now() - burst.firstAt;
  diagLog('Perf',
    `wall: sceneReady n=${burst.count} handlerSum=${ms(burst.handlerSum)} max=${ms(burst.max)} ` +
    `gapSum=${ms(burst.gapSum)}; storeSet=${ms(store.setMs)}; ` +
    `boardRenders=${board.renders} renderSum=${ms(board.bodyMs)} commitSum=${ms(board.commitMs)}; ` +
    `scenes=${board.scenes} cellsMounted=${board.cells}; sinceFirstSceneReady=${ms(sinceFirst)}`);
  reset();
}

/** The store swap at the head of loadProject — the other candidate for the wall, measured separately. */
export function emitLoadProjectSwapPerf(setStateMs: number, scenes: number, shots: number): void {
  diagLog('Perf', `loadProject swap: setState=${ms(setStateMs)} scenes=${scenes} shots=${shots}`);
}

/**
 * loadBridgeHistoryAssets — the ruled-out suspect, on the record. Emitted from inside the async
 * body at completion rather than at the call site: the call site is fire-and-forget, so it would
 * only ever be able to report zeros it hadn't measured.
 */
export function emitHistoryAssetsPerf(
  entries: number, fileFetches: number, setStates: number, elapsedMs: number,
): void {
  diagLog('Perf', `historyAssets: entries=${entries} fileFetches=${fileFetches} setStates=${setStates} ms=${ms(elapsedMs)}`);
}
